import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ListPlus, Share2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import InterestForm from "@/components/InterestForm";
import FutureSessionMeter from "@/components/FutureSessionMeter";
import { useScheduleOptions } from "@/hooks/useScheduleOptions";
import { toast } from "sonner";
import { slugOf } from "@/lib/classRegistry";
import type { ClassItem } from "@/data/classes";

interface Props {
  dbClassId: string | null | undefined;
  publicClassId: string;
  className: string;
  /** Full class item — used to build the deep-link for Share. */
  classItem?: ClassItem;
}

/**
 * Public "Potential Future Sessions" panel. Renders every non-hidden schedule
 * option with a demand-planning meter, share link, and interest CTA.
 *
 * URL deep-link support: `?option=<schedule_option_id>` auto-expands the
 * matching option and scrolls it into view.
 */
const AdditionalTimeOptions = ({ dbClassId, publicClassId, className, classItem }: Props) => {
  const { options, reload } = useScheduleOptions(dbClassId);
  const [openId, setOpenId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [params] = useSearchParams();
  const highlightId = params.get("option");
  const highlightRef = useRef<HTMLDivElement | null>(null);
  const didAutoOpen = useRef(false);

  const visible = options.filter((o) => o.status !== "hidden");
  const interestOptions = visible.filter((o) => o.status === "interest_only");

  // Some interest-only options are age-group / cohort buckets rather than a
  // weekday (day_of_week = "TBD"). Copy must never read "Interested in TBDs".
  const isWeekday = (d: string | null | undefined) =>
    /^(mon|tues|wednes|thurs|fri|satur|sun)day$/i.test((d ?? "").trim());

  // Auto-open + scroll the deep-linked option once options load.
  useEffect(() => {
    if (didAutoOpen.current) return;
    if (!highlightId) return;
    const match = visible.find((o) => o.id === highlightId);
    if (!match) return;
    didAutoOpen.current = true;
    if (match.status === "interest_only") setOpenId(match.id);
    setTimeout(() => {
      highlightRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
  }, [highlightId, visible]);

  if (visible.length === 0) return null;

  const shareOption = async (optionId: string) => {
    const slug = classItem ? slugOf(classItem) : "";
    const url = new URL(window.location.origin + "/classes");
    if (slug) url.searchParams.set("class", slug);
    url.searchParams.set("option", optionId);
    try {
      await navigator.clipboard.writeText(url.toString());
      setCopiedId(optionId);
      toast.success("Link copied — share it with another homeschool family!");
      setTimeout(() => setCopiedId((c) => (c === optionId ? null : c)), 2000);
    } catch {
      toast.error("Couldn't copy the link. Long-press the address bar to share instead.");
    }
  };

  return (
    <div className="mt-6 space-y-3">
      <div>
        <h4 className="font-heading text-lg text-foreground">Potential Future Sessions</h4>
        <p className="text-xs text-muted-foreground">
          Times we may open based on family interest. Adding your name doesn't enroll you.
        </p>
      </div>
      {visible.map((opt) => {
        const isInterest = opt.status === "interest_only";
        const isOpen = opt.status === "open";
        const isExpanded = openId === opt.id;
        const highlighted = highlightId === opt.id;
        const reached =
          isInterest && opt.interest_threshold > 0 && opt.interest_count >= opt.interest_threshold;
        return (
          <div
            key={opt.id}
            ref={highlighted ? highlightRef : undefined}
            className={`rounded-lg border p-4 space-y-3 transition-shadow ${
              highlighted
                ? "border-primary/60 bg-primary/5 shadow-md ring-1 ring-primary/40"
                : "border-sky-200 bg-sky-50/60"
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-heading text-base font-bold text-sky-900">
                    {opt.display_label}
                  </p>
                  <Badge
                    variant="outline"
                    className={
                      isInterest
                        ? "bg-amber-50 text-amber-800 border-amber-200 text-[10px]"
                        : isOpen
                          ? "bg-emerald-50 text-emerald-800 border-emerald-200 text-[10px]"
                          : "bg-neutral-100 text-neutral-700 border-neutral-200 text-[10px]"
                    }
                  >
                    {isInterest ? "Coming Soon" : isOpen ? "Open" : "Closed"}
                  </Badge>
                </div>
                <p className="text-sm font-medium text-sky-900 mt-0.5">
                  {isWeekday(opt.day_of_week)
                    ? `${opt.day_of_week}s, ${opt.start_time}${opt.end_time ? `–${opt.end_time}` : ""}`
                    : "Schedule TBD"}
                </p>
                {isInterest && (
                  <p className="text-sm text-sky-900 mt-1">
                    {reached
                      ? "This group may open soon. Add your name so we know to include your family."
                      : `We may open ${opt.display_label} once enough families are interested. Share it with another homeschool family to help unlock the class!`}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => shareOption(opt.id)}
                className="shrink-0 inline-flex items-center gap-1 text-xs text-sky-800 hover:text-sky-900 hover:underline"
                aria-label="Share this option"
              >
                {copiedId === opt.id ? <Check className="h-3.5 w-3.5" /> : <Share2 className="h-3.5 w-3.5" />}
                {copiedId === opt.id ? "Copied" : "Share"}
              </button>
            </div>

            {isInterest && (
              <FutureSessionMeter count={opt.interest_count} threshold={opt.interest_threshold} />
            )}

            {isInterest && !isExpanded && (
              <Button
                type="button"
                className="w-full font-body font-bold"
                onClick={() => setOpenId(opt.id)}
              >
                <ListPlus size={16} className="mr-2" />
                {reached ? "Join the Interest List" : `I'm Interested in ${opt.display_label}`}
              </Button>
            )}

            {isInterest && isExpanded && (
              <InterestForm
                classId={publicClassId}
                className={className}
                title={`Join the ${opt.display_label} interest list`}
                intro={`Tell us about your child for ${opt.display_label}${
                  isWeekday(opt.day_of_week)
                    ? ` (${opt.day_of_week}s, ${opt.start_time}${opt.end_time ? `–${opt.end_time}` : ""})`
                    : ""
                }. This expresses interest only — it does not register your child and does not charge you anything.`}
                presetOption={{
                  label: opt.display_label,
                  weekday: opt.day_of_week,
                  option: opt.display_label,
                }}
                // Options are NOT mutually exclusive: one child may be
                // interested in several (e.g. an age group + Dad & Me).
                // Each checked option becomes its own structured selection
                // row against the same child — never a duplicate child.
                optionChoices={
                  interestOptions.length > 1
                    ? interestOptions.map((o) => ({
                        id: o.id,
                        label: o.display_label,
                        weekday: o.day_of_week,
                        scheduleLine: isWeekday(o.day_of_week)
                          ? `${o.day_of_week}s, ${o.start_time}${o.end_time ? `–${o.end_time}` : ""}`
                          : "Schedule TBD",
                      }))
                    : undefined
                }
                optionChoicesHelp="Select all that apply — one entry covers every option you choose for this child."
                onSubmitted={() => reload()}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AdditionalTimeOptions;
